/**
 * FilterModal - renders advanced-placement filter plugins with draft state.
 *
 * DD-4: Clones values on open, edits locally (draft).
 * Apply commits draft to parent (URL), ESC/Cancel discards, Clear resets to defaults.
 *
 * Accessibility:
 * - role="dialog" aria-modal="true" aria-labelledby={titleId}
 * - Focus trap while open
 * - Return focus to trigger element on close
 *
 * @see docs/specifications/2026-03-10-plugin-filter-architecture.md (Section 3.5)
 */

import React, { useState, useEffect, useCallback, useRef, useId } from "react";
import { FilterPlugin } from "./types";
import { getAdvancedFilters } from "./registry";

export interface FilterModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** The filter plugin registry */
  registry: ReadonlyArray<FilterPlugin<unknown>>;
  /** Current committed filter values (from URL) */
  filterValues: Record<string, unknown>;
  /** Faceted search counts from backend (passed through to plugins) */
  facets?: Record<string, unknown>;
  /** Callback when user applies draft values */
  onApply: (draftValues: Record<string, unknown>) => void;
  /** Callback when user closes/cancels the modal */
  onClose: () => void;
}

export const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  registry,
  filterValues,
  facets,
  onApply,
  onClose,
}) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const advancedPlugins = getAdvancedFilters(registry);

  // Draft state - cloned from filterValues when modal opens
  const [draft, setDraft] = useState<Record<string, unknown>>({});

  // Clone filter values into draft when modal opens
  useEffect(() => {
    if (isOpen) {
      const draftValues: Record<string, unknown> = {};
      for (const plugin of advancedPlugins) {
        draftValues[plugin.key] =
          filterValues[plugin.key] ?? plugin.defaultValue;
      }
      setDraft(draftValues);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ESC key handler
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Focus trap: focus the dialog when opened
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [isOpen]);

  // Update a single draft value
  const setDraftValue = useCallback((key: string, value: unknown) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Apply draft values
  const handleApply = useCallback(() => {
    onApply(draft);
    onClose();
  }, [draft, onApply, onClose]);

  // Clear all draft values to defaults
  const handleClear = useCallback(() => {
    const defaults: Record<string, unknown> = {};
    for (const plugin of advancedPlugins) {
      defaults[plugin.key] = plugin.defaultValue;
    }
    setDraft(defaults);
  }, [advancedPlugins]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 outline-none"
      >
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2
            id={titleId}
            className="text-lg font-medium text-neutral-900"
          >
            Więcej filtrów
          </h2>
          <p className="mt-1 text-sm text-neutral-600">
            Dodatkowe opcje filtrowania naborów
          </p>
        </div>

        <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {advancedPlugins.map((plugin) => {
            // Hidden plugins (Component: null) are filtered out by getAdvancedFilters,
            // but guard here for type safety.
            if (!plugin.Component) return null;
            const PluginComponent = plugin.Component;
            const value = draft[plugin.key] ?? plugin.defaultValue;
            return (
              <div key={plugin.key}>
                <PluginComponent
                  value={value}
                  onChange={(newValue: unknown) =>
                    setDraftValue(plugin.key, newValue)
                  }
                  facets={facets}
                />
              </div>
            );
          })}
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 flex justify-between items-center">
          <button
            type="button"
            onClick={handleClear}
            className="text-sm font-medium text-neutral-600 hover:text-neutral-900"
            aria-label="Wyczyść wszystkie filtry zaawansowane"
          >
            Wyczyść wszystkie
          </button>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-md hover:bg-neutral-200"
              aria-label="Anuluj i zamknij"
            >
              Anuluj
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              aria-label="Zastosuj filtry"
            >
              Zastosuj filtry
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
